import BaseCurrencyInput, {
  CurrencyInputOnChangeValues,
} from 'react-currency-input-field';

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import { getCurrencySymbol, useTranslation } from '@splitfiesta/i18n';
import { getCurrencyDecimals } from '@splitfiesta/shared';

import { formInputClasses, getInputClasses } from '@/lib/styles/form';

interface CurrencyInputProps {
  id?: string;
  name?: string;
  value?: string;
  currency: string;
  onValueChange?: (
    value: string | undefined,
    name?: string,
    values?: CurrencyInputOnChangeValues
  ) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  'data-testid'?: string;
}

export interface CurrencyInputRef {
  focus: () => void;
}

// Helper function to get locale-specific number format info
function getLocaleNumberFormat(locale: string) {
  try {
    // Create a test number with decimal and thousands separators
    const testNumber = 1234.56;
    const formatted = new Intl.NumberFormat(locale).format(testNumber);

    // Extract separators from the formatted string
    // Format will be something like "1,234.56" (en-US) or "1.234,56" (de-DE)
    const decimalSeparator =
      formatted.includes(',') && formatted.indexOf(',') > formatted.indexOf('.')
        ? ','
        : '.';
    const groupSeparator = decimalSeparator === ',' ? '.' : ',';

    return { decimalSeparator, groupSeparator };
  } catch (_error) {
    // Fallback to US format
    return { decimalSeparator: '.', groupSeparator: ',' };
  }
}

export const CurrencyInput = forwardRef<CurrencyInputRef, CurrencyInputProps>(
  function CurrencyInput(
    {
      id,
      name,
      value,
      currency,
      onValueChange,
      className,
      placeholder,
      disabled = false,
      required = false,
      autoFocus = false,
      'data-testid': testId,
    },
    ref
  ) {
    const { i18n: _i18n } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);

    // Expose focus method to parent components
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
    }));

    // Clean the value by removing non-breaking spaces
    // The react-currency-input-field library adds these internally
    const cleanedValue = value?.replace(/\u00A0/g, ' ').trim() || '';

    // Handle focus event to clean trailing spaces
    const handleFocus = useCallback(() => {
      if (inputRef.current && inputRef.current.value) {
        const currentValue = inputRef.current.value;
        // Remove non-breaking spaces and trim
        const cleaned = currentValue.replace(/\u00A0/g, ' ').trim();
        if (currentValue !== cleaned) {
          inputRef.current.value = cleaned;
        }
      }
    }, []);

    // Use the system's number-formatting locale so separators match what
    // the device keyboard offers. navigator.language reflects the UI language,
    // but iOS keyboards use the Region setting to decide the decimal key.
    // Intl.NumberFormat() with no arguments resolves to the Region-based locale.
    const userLocale = new Intl.NumberFormat().resolvedOptions().locale;
    const decimalsLimit = getCurrencyDecimals(currency);
    const { decimalSeparator, groupSeparator: _groupSeparator } =
      getLocaleNumberFormat(userLocale);

    // Helper to get currency-specific placeholder with correct separator
    const getCurrencyPlaceholder = (currencyCode: string) => {
      if (!currencyCode) return '0' + decimalSeparator + '00';
      const decimals = getCurrencyDecimals(currencyCode);
      if (decimals === 0) return '0';
      return '0' + decimalSeparator + '0'.repeat(decimals);
    };

    const finalPlaceholder = placeholder || getCurrencyPlaceholder(currency);

    // Intercept decimal separator keystrokes BEFORE they enter the DOM.
    // When the user types '.' but the locale expects ',', swap it (and vice versa).
    // This handles iOS/Android keyboard mismatches without corrupting
    // library-formatted group separators (which transformRawValue did).
    useEffect(() => {
      const el = inputRef.current;
      if (!el) return;

      const handler = (e: InputEvent) => {
        if (e.inputType !== 'insertText') return;
        if (e.data === '.' && decimalSeparator === ',') {
          e.preventDefault();
          document.execCommand('insertText', false, ',');
        } else if (e.data === ',' && decimalSeparator === '.') {
          e.preventDefault();
          document.execCommand('insertText', false, '.');
        }
      };

      el.addEventListener('beforeinput', handler as EventListener);
      return () =>
        el.removeEventListener('beforeinput', handler as EventListener);
    }, [decimalSeparator]);

    // Use consistent input styling with dark mode support
    // Build input classes manually when we have size overrides
    const inputClasses = className?.includes('!text-')
      ? `${formInputClasses.base} ${formInputClasses.focus} ${
          disabled ? formInputClasses.disabled : formInputClasses.hover
        } ${className}`.trim()
      : getInputClasses({
          disabled,
          size: 'sm',
          className: className || '',
        });

    return (
      <BaseCurrencyInput
        ref={inputRef}
        id={id}
        name={name}
        value={cleanedValue}
        decimalsLimit={decimalsLimit}
        onValueChange={onValueChange}
        onFocus={handleFocus}
        className={inputClasses}
        placeholder={finalPlaceholder}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        allowNegativeValue={false}
        allowDecimals={decimalsLimit > 0}
        step={decimalsLimit > 0 ? Math.pow(10, -decimalsLimit) : 1}
        disableGroupSeparators={false}
        disableAbbreviations={true}
        prefix={getCurrencySymbol(currency)}
        intlConfig={{
          locale: userLocale,
          currency: currency,
        }}
        autoComplete="off"
        data-testid={testId}
      />
    );
  }
);

export default CurrencyInput;
